import {
  findParentNode,
  findParentNodeClosestToPos,
  mergeAttributes,
  Node,
} from '@tiptap/core'
import { Plugin, TextSelection } from 'prosemirror-state'
import { nanoid } from 'nanoid'
import {
  generateCustomListCorrectionMarkup,
  validateCustomListNumbers,
} from './validateCustomListNumbers.mjs'
import { validateCustomListIds } from './validateCustomListIds.js'
import { listItemConfigurations } from './listItemConfigurations.js'
import { ensureDistributedListIds } from './ensureDistributedListIds.js'
import { validateCustomListMultipleParagraphs } from './validateCustomListMultipleParagraphs.mjs'
import { validateCustomListNonEmptyDocument } from './validateCustomListNonEmptyDocument.mjs'

export function createCustomListItemExtension(nodeViewRenderer) {
  const opts = {
    name: 'customListItem',
    selectable: false,

    priority: 101,
    group: 'block',
    content: 'paragraph+',

    addOptions() {
      return {
        listableNodeTypes: [],
      }
    },

    parseHTML() {
      return [
        {
          tag: 'custom-list',
        },
      ]
    },

    renderHTML({ HTMLAttributes }) {
      return ['custom-list', mergeAttributes(HTMLAttributes), 0]
    },

    addAttributes() {
      // Return an object with attribute configuration
      return {
        depth: {
          default: 0,
        },
        'list-style-type': {
          default: 'ordered5',
        },
        'item-number': {
          default: 0,
        },
        'list-id': {
          default: null,
        },
        checked: {
          default: false,
        },
      }
    },

    // Add command to toggle class name
    addCommands() {
      return {
        ...this.parent?.(),
        toggleCustomList:
          (ordered = true, specificStyle = null) =>
          ({ tr }) => {
            let listStyleType
            if (specificStyle) {
              listStyleType = specificStyle
            }
            // iterate over the selected nodes and toggle the list style type
            let { from, to, $from, head } = tr.selection

            const headIsFrom = head === from

            const nodeRange = { from, to }
            const cursorDepth = $from.depth
            let fromCustomListNode, fromListableNode
            for (let i = cursorDepth; i > 0; i--) {
              const n = $from.node(i)
              const pos = $from.before(i)
              if (this.options.listableNodeTypes.includes(n.type.name)) {
                fromListableNode = { node: n, pos }
                nodeRange.from = Math.min(nodeRange.from, pos)
                nodeRange.to = Math.max(nodeRange.to, pos + n.nodeSize)
              } else if (n.type.name === 'customListItem') {
                fromCustomListNode = { node: n, pos }
                nodeRange.from = Math.min(nodeRange.from, pos)
                nodeRange.to = Math.max(nodeRange.to, pos + n.nodeSize)
              }
            }
            if (cursorDepth === 0) {
              // If the cursor is at the top level, we need to check if the cursor
              // is right before the start of a listable node
              const node = $from.nodeAfter
              if (
                node &&
                this.options.listableNodeTypes.includes(node.type.name)
              ) {
                fromListableNode = { node, pos: $from.pos }
              }
              if (node) {
                nodeRange.to = Math.max(nodeRange.to, $from.pos + node.nodeSize)
              }
            }

            let newSelection = { from, to }
            if (fromListableNode) {
              // extend the selection to the whole list item

              let $pos = tr.doc.resolve(fromListableNode.pos)

              let nearestNonListableNode = null
              let nearestNonListableNodePos = null

              // Walk backward to find the closest `customListItem`
              while ($pos.nodeBefore) {
                const prevNode = $pos.nodeBefore
                if (
                  !this.options.listableNodeTypes.includes(prevNode.type.name)
                ) {
                  nearestNonListableNode = prevNode
                  nearestNonListableNodePos = $pos.pos - prevNode.nodeSize
                  break
                }
                $pos = tr.doc.resolve($pos.pos - prevNode.nodeSize)
              }

              if (nearestNonListableNode) {
                if (
                  nearestNonListableNode.type.name === 'customListItem' ||
                  nearestNonListableNode.type.name === 'paragraph'
                ) {
                  nodeRange.from = nearestNonListableNodePos
                } else {
                  // insert a paragraph after the nearest non-listable node
                  const newParagraph =
                    this.editor.schema.nodes.paragraph.create()
                  tr.insert(
                    nearestNonListableNodePos + nearestNonListableNode.nodeSize,
                    newParagraph,
                  )
                  nodeRange.from =
                    nearestNonListableNodePos + nearestNonListableNode.nodeSize
                  nodeRange.to += newParagraph.nodeSize

                  newSelection.to += newParagraph.nodeSize
                  newSelection.from += newParagraph.nodeSize
                }
              }
            }

            const nodes = []
            let clearingListFormat = true

            let hasParagraphs = false
            let listStyleTypesFound = []
            let listIdsFound = []
            let uniqueListStyleTypesFound = new Set()
            let uniqueListIdsFound = new Set()
            tr.doc.nodesBetween(
              nodeRange.from,
              nodeRange.to,
              (node, pos, parent, index) => {
                let isParagraph = node.type.name === 'paragraph'
                let isListItem = node.type.name === 'customListItem'
                if (isParagraph) hasParagraphs = true
                if (isListItem) {
                  listStyleTypesFound.push(node.attrs['list-style-type'])
                  listIdsFound.push(node.attrs['list-id'])
                }
                if (isParagraph || isListItem) {
                  nodes.unshift({
                    node,
                    pos,
                  })
                }

                nodeRange.from = Math.min(nodeRange.from, pos)
                nodeRange.to = Math.max(nodeRange.to, pos + node.nodeSize)
                return false // Return false to prevent the traversal from going deeper
              },
            )
            uniqueListStyleTypesFound = new Set(listStyleTypesFound)
            uniqueListIdsFound = new Set(listIdsFound)
            clearingListFormat =
              !hasParagraphs &&
              uniqueListStyleTypesFound.size === 1 &&
              uniqueListIdsFound.size === 1 &&
              ((listStyleType && listStyleTypesFound[0] === listStyleType) ||
                (!listStyleType &&
                  listItemConfigurations[listStyleTypesFound[0]].type ===
                    (ordered ? 'ordered' : 'unordered')))

            if (nodes.length === 0) {
              clearingListFormat = false
              // insert a paragraph at the start of the selection
              const newParagraph = this.editor.schema.nodes.paragraph.create()
              tr.insert(nodeRange.from, newParagraph)
              nodeRange.to += newParagraph.nodeSize
              newSelection.to += newParagraph.nodeSize
              nodes.push({
                node: newParagraph,
                pos: nodeRange.from,
              })
            }
            let listId
            listStyleType ||= ordered ? 'ordered5' : 'unordered1'
            if (!clearingListFormat) {
              const firstNode = nodes[nodes.length - 1]

              let $pos = tr.doc.resolve(firstNode.pos)
              let nodeBeforeFirstNode
              let nodeBeforeFirstNodePos

              // Walk backward to find the closest `customListItem`
              while ($pos.nodeBefore) {
                const prevNode = $pos.nodeBefore
                if (
                  !this.options.listableNodeTypes.includes(prevNode.type.name)
                ) {
                  nodeBeforeFirstNode = prevNode
                  nodeBeforeFirstNodePos = $pos.pos - prevNode.nodeSize
                  break
                }
                $pos = tr.doc.resolve($pos.pos - prevNode.nodeSize)
              }
              let nearbyListNode = null

              if (
                nodeBeforeFirstNode?.type.name === 'customListItem' &&
                listStyleType === nodeBeforeFirstNode.attrs['list-style-type']
              ) {
                nearbyListNode = nodeBeforeFirstNode
              } else {
                const lastNode = nodes[0]

                let $pos = tr.doc.resolve(lastNode.pos + lastNode.node.nodeSize)
                // Walk forward to find the closest `customListItem`
                while ($pos.nodeAfter) {
                  const nextNode = $pos.nodeAfter
                  if (
                    !this.options.listableNodeTypes.includes(nextNode.type.name)
                  ) {
                    if (
                      nextNode.type.name === 'customListItem' &&
                      listStyleType === nextNode.attrs['list-style-type']
                    ) {
                      nearbyListNode = nextNode
                    }
                    break
                  }
                  $pos = tr.doc.resolve($pos.pos + nextNode.nodeSize)
                }
              }

              if (nearbyListNode) {
                console.warn('Found nearby list node:', nearbyListNode)
                listId = nearbyListNode.attrs['list-id']
              }
            }
            listId ||= nanoid()

            const depthsAffected = new Set()
            for (const { node, pos } of nodes) {
              if (node.type.name === 'customListItem') {
                if (clearingListFormat) {
                  const content = []
                  for (let i = 0; i < node.childCount; i++) {
                    const child = node.child(i)
                    content.push(child.copy(child.content))
                  }
                  tr.replaceWith(pos, pos + node.nodeSize, content)

                  const left = pos
                  const right = pos + node.nodeSize
                  newSelection.to -= to >= right ? 2 : to > left ? 1 : 0
                  newSelection.from -= from >= right ? 2 : from > left ? 1 : 0
                } else if (
                  node.attrs['list-style-type'] !== listStyleType ||
                  node.attrs['list-id'] !== listId
                ) {
                  tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    'list-id': listId,
                    'list-style-type': listStyleType,
                  })
                }
              } else if (node.type.name === 'paragraph') {
                if (!clearingListFormat) {
                  // We need to convert the paragraph to a list item
                  const newListItem =
                    this.editor.schema.nodes.customListItem.create(
                      {
                        ...node.attrs,
                        'list-id': listId,
                        'list-style-type': listStyleType,
                        'item-number': 0, // We'll just run the validation function to fix the item numbers
                      },
                      [node.copy(node.content)],
                    )
                  tr.replaceWith(pos, pos + node.nodeSize, [newListItem])

                  const left = pos
                  const right = pos + node.nodeSize
                  newSelection.to += to >= right ? 2 : to > left ? 1 : 0
                  newSelection.from += from >= right ? 2 : from > left ? 1 : 0
                }
              }
            }

            // Find the nearest text selection to the new selection

            if (!clearingListFormat && uniqueListIdsFound.size > 0) {
              // Iterate through all nodes in the document, adjusting each of the
              // unique list IDs found in the selection to share the same list ID
              // and list style type
              tr.doc.nodesBetween(0, tr.doc.content.size, (node, pos) => {
                if (
                  node.type.name === 'customListItem' &&
                  uniqueListIdsFound.has(node.attrs['list-id'])
                ) {
                  tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    'list-id': listId,
                    'list-style-type': listStyleType,
                  })
                }
              })
            }

            tr.setSelection(
              TextSelection.create(
                tr.doc,
                headIsFrom ? newSelection.to : newSelection.from,
                headIsFrom ? newSelection.from : newSelection.to,
              ),
            )
            tr.scrollIntoView()

            return true
          },
        spaceCustomList:
          () =>
          ({ chain, tr, commands }) => {
            // If the user starts typing in a list item, we need to call the
            // toggleCustomList command to ensure that the list item is properly
            // formatted

            // First check if the cursor just typed space after something like
            // '*' or '1.' or 'a.' or 'i.' or 'A.' or 'I.'

            // If it's '*' then the list-style-type should be 'unordered-standard'
            // And if it's '1.' then the list-style-type should be 'ordered-standard'
            // It should use chain or commands to call the toggleCustomList command
            // with the appropriate list-style-type
            const { $from, from } = tr.selection

            if (!tr.selection.empty) {
              return false
            }

            // if the cursor is already part of a list item, we don't need to do anything
            const parent = findParentNode(
              (node) => node.type.name === 'customListItem',
            )(tr.selection)
            if (parent?.node) {
              return false
            }

            const beforeText = $from.parent.textBetween(
              0,
              $from.parentOffset,
              null,
              '\0',
            )

            const types = {
              '1\\.': 'ordered1',
              '1\\)': 'ordered2',
              'A\\.': 'ordered4',
              'I\\.': 'ordered5',
              '01\\.': 'ordered6',
              '•': 'unordered1',
              '\\*': 'unordered1',
              '-': 'unordered4',
              '\\+': 'unordered2',
              '❖': 'unordered2',
              '☐': 'unordered3',
              '☑': 'unordered3',
              '➔': 'unordered4',
              '★': 'unordered5',
              '➢': 'unordered6',
            }

            const sortedTypes = Object.keys(types).sort((a, b) =>
              a.localeCompare(b),
            )
            const regexStr = `^\\s*(${sortedTypes.map((t) => `(${t})`).join('|')})`
            console.log('Final regex string:', regexStr)
            const regex = new RegExp(regexStr)

            // Check if the text before the cursor matches any of the list item patterns
            if (!regex.test(beforeText)) {
              // It doesn't match any of our supported patterns, but it may still match one of the built-in patterns,
              // namely ^\d+\.

              const builtInRegex = /^\s*\d+\.$/
              if (builtInRegex.test(beforeText)) {
                // Just add the space ourselves and return true.  This prevents
                // the built-in list from automatically triggering.
                tr.insertText(' ', from, from)
                return true
              }

              // If it doesn't match any of the patterns, we don't need to do anything
              return false
            }

            const match = beforeText.match(regex)
            if (!match) {
              // If the regex didn't match, we don't need to do anything
              return false
            }
            let style
            for (let i = 0; i < sortedTypes.length; i++) {
              if (match[i + 2]) {
                style = types[sortedTypes[i]]
                break
              }
            }
            if (!style) {
              // If we couldn't find a matching style, we don't need to do anything
              return false
            }
            console.log('Found list style type:', style, beforeText)
            const ordered = listItemConfigurations[style].type === 'ordered'

            return chain()
              .deleteRange({ from: from - beforeText.length, to: from })
              .command(() => commands.toggleCustomList(ordered, style))
              .focus()
              .run()
          },
        tabCustomList:
          (depthModifier = 1, tabChildren = true) =>
          ({ chain, tr }) => {
            const commandMap = new Map()
            const selection = tr.selection
            const nodeHelperResult = calculateNodeRange(
              tr.doc,
              selection,
              this.options,
            )
            // console.warn('Node helper result:', nodeHelperResult)
            let nodeRange = nodeHelperResult.selection
            let { $from, from, to, anchor, head } = selection
            // check if there is any listable nodes in the selection

            const allCustomListItems = []
            const customListItems = []
            const listHierarchy = new Map()
            let changed = false
            tr.doc.nodesBetween(
              nodeHelperResult.fullList.from,
              nodeHelperResult.fullList.to,
              (node, pos) => {
                if (node?.type?.name !== 'customListItem') {
                  return false
                }
                if (
                  nodeRange.from <= pos &&
                  pos + node.nodeSize <= nodeRange.to
                ) {
                  customListItems.push({ node, pos })
                }
                allCustomListItems.push({ node, pos })
                listHierarchy.set(
                  node.attrs['depth'],
                  node.attrs['list-style-type'],
                )
              },
            )

            for (let i = customListItems.length - 1; i >= 0; i--) {
              const { node, pos } = customListItems[i]

              let depth = Math.max(node.attrs.depth + depthModifier, 0)
              commandMap.set(pos, {
                ...node.attrs,
                depth,
              })
              changed = true
            }
            //TODO: Implement tabChildren - not a big deal but would be a nice
            // feature.  I think it would probably just continue on past
            // the last list item, and if there are any children customListItems
            // following the current customListItem, it would recursively tab
            // them as well.  And it might only do it if the selection is empty.

            if (!changed) {
              return false
            }

            generateCustomListCorrectionMarkup(
              tr.doc,
              this.options.listableNodeTypes,
              commandMap,
            )

            const positions = Array.from(commandMap.keys()).sort(
              (a, b) => a - b,
            )
            if (positions.length > 0) {
              for (let i = 0; i < positions.length; i++) {
                const pos = positions[i]
                const attrs = commandMap.get(pos)
                tr.setNodeMarkup(pos, undefined, attrs)
              }

              tr.setSelection(TextSelection.create(tr.doc, anchor, head))

              setTimeout(() => {
                this.editor.commands.scrollIntoView()
              })
            }

            return true
          },
        backspaceCustomList:
          () =>
          ({ chain, tr }) => {
            const { selection } = tr
            const { $from, from } = selection
            // We need to check if the cursor is at the beginning of the list item,
            // with none of the content selected.  If it is, we need to transform the list item into a paragraph
            const cursorDepth = $from.depth

            let listNode, noteBoardNoteNode
            for (let i = cursorDepth; i > 0; i--) {
              const n = $from.node(i)
              if (n.type.name === 'customListItem') {
                listNode = n
              } else if (n.type.name === 'noteBoardNote') {
                noteBoardNoteNode = n
              }
            }

            if ($from.parentOffset === 0 && selection.empty) {
              if (listNode && this.editor.isActive(this.name)) {
                // Case 1: We're at the beginning of a `customListItem`

                if (listNode.attrs['depth'] === 0) {
                  // If it's a top-level list item, lift it (convert to paragraph)
                  chain().lift().run()
                } else {
                  // If it's not a top-level list item, decrease the depth
                  chain().tabCustomList(-1, false).run()
                }
                return true
              } else {
                const prevPos = from - 1
                const $prevPos = tr.doc.resolve(prevPos)
                const prevNode = $prevPos.nodeBefore

                if (prevNode?.type.name === 'customListItem') {
                  // Case 2: We're at the start of a paragraph and need to check if
                  // the previous node is a `customListItem`.  If it is, we need to
                  // join the current paragraph directly with the previous list item
                  if (noteBoardNoteNode) {
                    // Just move the selection to the end of the previous node
                    tr.setSelection(TextSelection.create(tr.doc, from - 3))
                    return true
                  } else {
                    return chain().joinTextblockBackward().run()
                  }
                }
              }
            }
            return false
          },
        enterCustomList:
          () =>
          ({ chain, tr }) => {
            let parentNodeResult
            parentNodeResult = findParentNodeClosestToPos(
              tr.selection.$from,
              ({ type }) => this.options.listableNodeTypes.includes(type.name),
            )
            if (
              parentNodeResult &&
              parentNodeResult.node.attrs['list-depth'] >= 0
            ) {
              // The user has currently selected a single node, and it's a listable
              // node.  If the listable node has a depth >= 0, then it's a child of
              // a list item.  We need to find the nearest list item sibling and split it,
              // AFTER the current node.  We don't want to delete this node.

              let $pos = tr.doc.resolve(parentNodeResult.pos)
              let nearestNode = null

              // Walk backward to find the closest `customListItem`
              while ($pos.nodeBefore) {
                const prevNode = $pos.nodeBefore
                if (prevNode.type.name === 'customListItem') {
                  nearestNode = prevNode
                  break
                }
                $pos = tr.doc.resolve($pos.pos - prevNode.nodeSize)
              }

              if (nearestNode) {
                const newListItem =
                  this.editor.schema.nodes.customListItem.create(
                    {
                      'list-id': nearestNode.attrs['list-id'],
                      depth: nearestNode.attrs.depth,
                      'list-style-type': nearestNode.attrs['list-style-type'],
                      'item-number': nearestNode.attrs['item-number'] + 1,
                    },
                    [this.editor.schema.nodes.paragraph.create()],
                  )

                // Insert after the selected node
                const insertPos =
                  parentNodeResult.pos + parentNodeResult.node.nodeSize
                tr.insert(insertPos, newListItem)

                // Move cursor inside the new list item
                tr.setSelection(TextSelection.create(tr.doc, insertPos + 2))
                // focus on the selection
                tr.scrollIntoView()

                return true
              }
            }
            chain().deleteSelection().run()
            parentNodeResult = findParentNode(
              ({ type }) => type.name === 'customListItem',
            )(tr.selection)

            if (!parentNodeResult) {
              return false
            }

            const { node, pos } = parentNodeResult

            // Get the current selection and paragraph
            let selection = tr.selection
            const paragraphInNode = node.content.content[0]
            const paragraphPos = pos + 2

            const cutTo = Math.floor(selection.to - paragraphPos)
            const leftParagraph = paragraphInNode.cut(0, cutTo)
            const rightParagraph = paragraphInNode.cut(
              cutTo,
              paragraphInNode.nodeSize - 2,
            )
            // Now that we have both sides, we need to construct two new list items
            // and insert them into the document
            const leftListItem = this.editor.schema.nodes.customListItem.create(
              {
                'list-id': node.attrs['list-id'],
                depth: node.attrs.depth,
                'list-style-type': node.attrs['list-style-type'],
                'item-number': node.attrs['item-number'],
              },
              [leftParagraph],
            )

            const rightListItem =
              this.editor.schema.nodes.customListItem.create(
                {
                  'list-id': node.attrs['list-id'],
                  depth: node.attrs.depth,
                  'list-style-type': node.attrs['list-style-type'],
                  'item-number': node.attrs['item-number'] + 1,
                },
                [rightParagraph],
              )

            // Now we need to insert the new list items into the document

            tr.replaceWith(pos, pos + node.nodeSize, [
              leftListItem,
              rightListItem,
            ])
            tr.setSelection(
              TextSelection.create(tr.doc, pos + leftListItem.nodeSize + 2),
            )
            tr.scrollIntoView()
            return true
          },
      }
    },

    addKeyboardShortcuts() {
      return {
        Enter: () => this.editor.commands.enterCustomList(),
        Tab: () => this.editor.commands.tabCustomList(1),
        'Shift-Tab': () => this.editor.commands.tabCustomList(-1),
        Backspace: () => this.editor.commands.backspaceCustomList(),
        Space: () => this.editor.commands.spaceCustomList(),
        'Shift-Space': () => this.editor.commands.spaceCustomList(),
      }
    },

    addProseMirrorPlugins() {
      return [
        new Plugin({
          appendTransaction: (transactions, oldState, newState) => {
            let tr = newState.tr
            let schema = newState.schema
            // Validate the list items, ensuring that if there are any
            // contiguous list items, they have the correct item number

            let didAnythingChange = [
              // If there are multiple paragraphs in a list item, we need to
              // wrap them list items.  This handles copy-pasting from other
              // sources
              validateCustomListMultipleParagraphs(tr, schema),
              // Validate the item numbers
              ensureDistributedListIds(tr, this.options.listableNodeTypes),
              validateCustomListIds(tr, this.options.listableNodeTypes),
              validateCustomListNumbers(tr, this.options.listableNodeTypes),
              validateCustomListNonEmptyDocument(tr, schema),
            ].some(Boolean)

            return didAnythingChange ? tr : null
          },
        }),
      ]
    },
  }
  if (nodeViewRenderer) {
    opts.addNodeView = () => nodeViewRenderer
  }
  return Node.create(opts)
}

function calculateNodeRange(doc, selection, options) {
  let { $from, $to, from, to, anchor, head } = selection
  // check if there is any listable nodes in the selection

  let nodeRange = { from, to }

  let fromCustomListNode, fromListableNode
  for (let i = $from.depth; i > 0; i--) {
    const leftNode = $from.node(i)
    const pos = $from.before(i)
    if (
      options.listableNodeTypes.includes(leftNode.type.name) ||
      leftNode.type.name === 'customListItem'
    ) {
      nodeRange.from = Math.min(nodeRange.from, pos)
      nodeRange.to = Math.max(nodeRange.to, pos + leftNode.nodeSize)
      if (leftNode.type.name === 'customListItem') {
        fromCustomListNode = { node: leftNode, pos }
      } else {
        fromListableNode = { node: leftNode, pos }
      }
    }
  }
  let toCustomListNode, toListableNode
  for (let i = $to.depth; i > 0; i--) {
    const rightNode = $to.node(i)
    const toPos = $to.before(i)
    if (
      options.listableNodeTypes.includes(rightNode.type.name) ||
      rightNode.type.name === 'customListItem'
    ) {
      nodeRange.from = Math.min(nodeRange.from, toPos)
      nodeRange.to = Math.max(nodeRange.to, toPos + rightNode.nodeSize)
    }
    if (rightNode.type.name === 'customListItem') {
      toCustomListNode = { node: rightNode, pos: toPos }
    } else {
      toListableNode = { node: rightNode, pos: toPos }
    }
  }

  if ($from.depth === 0) {
    // If the cursor is at the top level, we need to check if the cursor
    // is right before the start of a listable node
    const node = $from.nodeAfter
    if (node) {
      nodeRange.from = Math.min(nodeRange.from, $from.pos)
      nodeRange.to = Math.max(nodeRange.to, $from.pos + node.nodeSize)

      if (node.type.name === 'customListItem') {
        fromCustomListNode = { node, pos: $from.pos }
      } else {
        fromListableNode = { node, pos: $from.pos }
      }
    }
  }

  if ($to.depth === 0) {
    const node = $to.nodeBefore
    if (node) {
      nodeRange.from = Math.min(nodeRange.from, $to.pos - node.nodeSize)
      nodeRange.to = Math.max(nodeRange.to, $to.pos)

      if (node.type.name === 'customListItem') {
        toCustomListNode = { node, pos: $to.pos - node.nodeSize }
      } else {
        toListableNode = { node, pos: $to.pos - node.nodeSize }
      }
    }
  }

  // if the first node isn't a custom list item, extend the selection to include the whole list item
  if (fromListableNode) {
    let $pos = doc.resolve(fromListableNode.pos)
    let node = fromListableNode.node
    let nodePos = fromListableNode.pos

    // Walk backward to find the closest `customListItem`
    while ($pos.nodeBefore) {
      const prevNode = $pos.nodeBefore
      if (prevNode.type.name === 'customListItem') {
        node = prevNode
        nodePos = $pos.pos - prevNode.nodeSize
        break
      } else if (!options.listableNodeTypes.includes(prevNode.type.name)) {
        break
      }
      $pos = doc.resolve($pos.pos - prevNode.nodeSize)
    }
    nodeRange.from = Math.min(nodeRange.from, nodePos)
  }

  const fullListRange = { from: nodeRange.from, to: nodeRange.to }
  // travel back to include the whole contiguous list
  let $pos = doc.resolve(nodeRange.from)
  while (
    $pos.nodeBefore &&
    ($pos.nodeBefore.type.name === 'customListItem' ||
      options.listableNodeTypes.includes($pos.nodeBefore.type.name))
  ) {
    $pos = doc.resolve($pos.pos - $pos.nodeBefore.nodeSize)
    fullListRange.from = $pos.pos
  }
  // travel forward to include the whole contiguous list
  $pos = doc.resolve(nodeRange.to)
  while (
    $pos.nodeAfter &&
    ($pos.nodeAfter.type.name === 'customListItem' ||
      options.listableNodeTypes.includes($pos.nodeAfter.type.name))
  ) {
    $pos = doc.resolve($pos.pos + $pos.nodeAfter.nodeSize)
    fullListRange.to = $pos.pos
  }

  return {
    selection: nodeRange,
    fullList: fullListRange,
  }
}
